import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, ApiError } from '../lib/api.js';
import { askConfirm } from '../lib/dialogs.js';
import { useFetch } from '../lib/useFetch.js';
import { providerName, useProviders } from '../lib/providers.js';
import { Async } from '../components/States.js';
import { useSession, canManage } from '../auth/session.js';
import { isSystemForm } from '../lib/systemForms.js';
import { syncBuilderForms } from '../lib/builderSync.js';
import { DateField, TimeField } from '../components/DateTimeField.js';
import { Dd } from '../components/Dropdown.js';
import { timeOpts } from '../lib/timePrefs.js';
import {
  clientOptions,
  clientPlaceholder,
  isClientRefusal,
  localDay,
  sendBlockReason,
  uniqueClients,
} from '../lib/sendChecks.js';

interface Patient {
  id: string;
  firstName: string;
  lastName: string;
  /** credifyfbs#309: the Client ID that tells same-name clients apart. */
  mrn?: string | null;
}

/**
 * credifyfbs#148: the client picker lists every active client CONTACT (all pages), not the
 * legacy `patients` table. `patients` only holds the clients that existed when Contacts
 * replaced it (same ids), so clients created since (e.g. "Portal Test 12:15 PM") never
 * appeared here. The dispatch API mirrors a contact into `patients` on first send.
 */
async function loadClientContacts(): Promise<Patient[]> {
  const out: Patient[] = [];
  let cursor: string | null = null;
  for (let page = 0; page < 20; page++) {
    const q: string = cursor ? '&cursor=' + encodeURIComponent(cursor) : '';
    const r: ClientPage = await api.get<ClientPage>('/contacts?category=client&limit=100' + q);
    out.push(...(r.items ?? []));
    cursor = r.nextCursor ?? null;
    if (!cursor) break;
  }
  const name = (p: Patient) => `${p.firstName} ${p.lastName}`;
  // credifyfbs#309: one row per client, even if a page boundary repeats one.
  return uniqueClients(out).sort((a, b) =>
    name(a).localeCompare(name(b), undefined, { sensitivity: 'base' }),
  );
}
interface ClientPage {
  items?: Patient[];
  nextCursor?: string | null;
}
interface Named {
  id: string;
  name: string;
  description?: string | null;
  source?: string | null;
  externalId?: string | null;
}
interface PacketWithCount extends Named {
  _count?: { packetForms: number };
}
interface PacketDetail {
  id: string;
  name: string;
  description: string | null;
  packetForms: { form: { id: string; name: string } }[];
}
interface Dispatch {
  id: string;
  destination: string;
  sentAt: string;
  patient: { firstName: string; lastName: string } | null;
  form: { name: string } | null;
  packet: { name: string } | null;
}

/** A scheduled send, held on the server (GET /dispatch/scheduled); the worker sends it at
 *  `sendAt`. Pending and failed ones are listed; a failed one carries the reason. */
interface ScheduledSend {
  id: string;
  patientId: string;
  patientName: string;
  packetId: string | null;
  packetName: string | null;
  formId: string | null;
  formName: string | null;
  destination: string;
  note: string;
  dueDate: string | null;
  sendAt: string;
  assignToId: string | null;
  assignName: string;
  status: 'pending' | 'failed';
  error: string | null;
}

/** Scheduled sends used to live only in this browser (nothing ever sent them). */
interface LegacyScheduledSend {
  patientId: string;
  packetId: string;
  dest?: string;
  assignId?: string;
  note?: string;
  dueDate?: string;
  sendDate: string;
  sendTime: string;
}
const SCHED_KEY = 'ehr.formsHub.scheduledSends';
const loadLegacyScheduled = (): LegacyScheduledSend[] => {
  try {
    const raw = localStorage.getItem(SCHED_KEY);
    const arr = raw ? (JSON.parse(raw) as LegacyScheduledSend[]) : [];
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
};

// Mockup OPTS.dest labels.
const DEST_LABEL: Record<string, string> = {
  portal: 'Client Portal',
  queue: 'Clinician Queue',
  both: 'Both',
};

const plusDays = (n: number) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return localDay(d);
};
const daysFromToday = (isoDate: string): number => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(isoDate + 'T00:00:00');
  // credifyfbs#308: no longer clamped to 0. A past date is blocked here, and the API refuses it.
  return Math.round((due.getTime() - today.getTime()) / 86400_000);
};
const schedDateTime = (s: ScheduledSend) => new Date(s.sendAt);
const localTimeHHMM = (d: Date) =>
  `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
const formatSchedWhen = (s: ScheduledSend) => {
  const d = schedDateTime(s);
  if (isNaN(d.getTime())) return s.sendAt;
  return (
    d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) +
    ', ' +
    d.toLocaleTimeString('en-US', timeOpts())
  );
};
const fmtSent = (s: string) => {
  const dt = new Date(s);
  const today = new Date();
  const same = dt.toDateString() === today.toDateString();
  const t = dt.toLocaleTimeString('en-US', timeOpts());
  return same ? `Today ${t}` : dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const SendSvg = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M22 2L11 13" />
    <path d="M22 2L15 22l-4-9-9-4 20-7z" />
  </svg>
);
const SaveSvg = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
    <polyline points="17 21 17 13 7 13 7 21" />
    <polyline points="7 3 7 8 15 8" />
  </svg>
);

/** fbs#158: a search match, marked in a bundle / form name. */
function Hl({ text, q }: { text: string; q: string }) {
  if (!q) return <>{text}</>;
  const lower = text.toLowerCase();
  const parts: ReactNode[] = [];
  let i = 0;
  let k = 0;
  for (let at = lower.indexOf(q); at >= 0; at = lower.indexOf(q, i)) {
    if (at > i) parts.push(text.slice(i, at));
    parts.push(
      <mark className="fh-hl" key={k++}>
        {text.slice(at, at + q.length)}
      </mark>,
    );
    i = at + q.length;
  }
  if (i < text.length) parts.push(text.slice(i));
  return <>{parts}</>;
}

const DEST_SUFFIX: Record<string, string> = {
  portal: 'to Portal',
  queue: 'to Clinician Queue',
  both: 'to Portal + Queue',
};

export function SendTab() {
  const { user } = useSession();
  const manage = canManage(user?.role);
  const patients = useFetch<Patient[]>(() => loadClientContacts(), []); // fbs#148: Contacts, not seed patients
  const forms = useFetch<Named[]>(() => api.get('/forms'), []);
  const packets = useFetch<PacketWithCount[]>(() => api.get('/packets'), []);
  const history = useFetch<Dispatch[]>(() => api.get('/dispatch/history'), []);
  // ehr#232 - providers only (they see clients), not the whole team.
  const providers = useProviders();

  // Per-packet member forms (mockup packet cards show form chips). Fetched once
  // per packet list load — the list endpoint only returns counts.
  const [packetForms, setPacketForms] = useState<Record<string, { id: string; name: string }[]>>(
    {},
  );
  useEffect(() => {
    const list = packets.data ?? [];
    if (!list.length) return;
    let live = true;
    Promise.all(
      list.map((p) =>
        api
          .get<PacketDetail>(`/packets/${p.id}`)
          .then((d) => [p.id, d.packetForms.map((pf) => pf.form)] as const)
          .catch(() => [p.id, []] as const),
      ),
    ).then((entries) => {
      if (live) setPacketForms(Object.fromEntries(entries));
    });
    return () => {
      live = false;
    };
  }, [packets.data]);

  const [selectedPacketId, setSelectedPacketId] = useState<string | null>(null);
  // fbs#158: send a bundle, or one or more individual forms; one search for both.
  const [what, setWhat] = useState<'bundles' | 'forms'>('bundles');
  const [query, setQuery] = useState('');
  const [selectedFormIds, setSelectedFormIds] = useState<string[]>([]);
  const [patientId, setPatientId] = useState('');
  const [dueDate, setDueDate] = useState(() => plusDays(7));
  const [destination, setDestination] = useState('portal');
  const [assignId, setAssignId] = useState('');
  const [note, setNote] = useState('');
  const [timing, setTiming] = useState<'now' | 'later'>('now');
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const [editingScheduledId, setEditingScheduledId] = useState<string | null>(null);
  const scheduledQ = useFetch<ScheduledSend[]>(() => api.get('/dispatch/scheduled'), []);
  const scheduled = scheduledQ.data ?? [];
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  // Packet builder slide-in
  const [pbOpen, setPbOpen] = useState(false);
  const [pbEditId, setPbEditId] = useState<string | null>(null);
  const [pbName, setPbName] = useState('');
  const [pbDesc, setPbDesc] = useState('');
  const [pbFormIds, setPbFormIds] = useState<string[]>([]);
  const [pbSearch, setPbSearch] = useState('');
  const [pbBusy, setPbBusy] = useState(false);
  const [pbError, setPbError] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [dragOver, setDragOver] = useState<number | null>(null);

  // Scheduled sends saved in this browser before they moved to the server: move the ones
  // still in the future to the server (the worker sends them); say how many had already
  // passed without being sent. Runs once; the local copy is removed after.
  useEffect(() => {
    const legacy = loadLegacyScheduled();
    if (!legacy.length) return;
    // Take them out first, so a second mount (or tab) can't move the same ones twice.
    try {
      localStorage.removeItem(SCHED_KEY);
    } catch {
      /* storage unavailable */
    }
    const future = legacy.filter(
      (s) => new Date(`${s.sendDate}T${s.sendTime || '00:00'}`).getTime() > Date.now(),
    );
    Promise.allSettled(
      future.map((s) =>
        api.post('/dispatch/scheduled', {
          patientId: s.patientId,
          packetId: s.packetId,
          destination: s.dest || 'portal',
          note: s.note || null,
          dueDate: s.dueDate || null,
          sendAt: new Date(`${s.sendDate}T${s.sendTime || '00:00'}`).toISOString(),
          assignToId: s.assignId || null,
        }),
      ),
    ).then((res) => {
      // Keep only the ones the server couldn't take right now (offline / 5xx) for next time;
      // a 4xx (client archived, bundle deleted) won't succeed later either.
      const retry = future.filter((_, i) => {
        const r = res[i];
        if (!r || r.status === 'fulfilled') return false;
        const e = r.reason as unknown;
        return !(e instanceof ApiError) || e.status >= 500;
      });
      if (retry.length) {
        try {
          localStorage.setItem(SCHED_KEY, JSON.stringify(retry));
        } catch {
          /* storage unavailable */
        }
      }
      const moved = res.filter((r) => r.status === 'fulfilled').length;
      const missed = legacy.length - future.length;
      const lost = future.length - moved - retry.length;
      const parts: string[] = [];
      if (moved) parts.push(`${moved} scheduled send${moved === 1 ? '' : 's'} moved to the server`);
      if (missed)
        parts.push(
          `${missed} saved in this browser had already passed and ${missed === 1 ? 'was' : 'were'} never sent`,
        );
      if (lost) parts.push(`${lost} could not be moved (client or bundle no longer available)`);
      if (parts.length) setMsg(`📅 ${parts.join('; ')}.`);
      scheduledQ.reload();
    });
  }, []);

  // Sendable forms = the built-in system screeners plus any forms synced from the
  // Form Builder (source "builder", imported via POST /forms/import-builder, #21).
  const sendableForms = (forms.data ?? []).filter(
    (f) => isSystemForm(f.name) || f.source === 'builder',
  );
  // A packet's member chips: show every form it actually contains.
  const memberChips = (packetId: string) => packetForms[packetId] ?? [];

  // fbs#158: one search over bundles (name, description, member forms) and forms.
  const q = query.trim().toLowerCase();
  const bundleList = (packets.data ?? []).filter(
    (p) =>
      !q ||
      p.name.toLowerCase().includes(q) ||
      (p.description ?? '').toLowerCase().includes(q) ||
      memberChips(p.id).some((f) => f.name.toLowerCase().includes(q)),
  );
  const formList = sendableForms.filter(
    (f) =>
      !q || f.name.toLowerCase().includes(q) || (f.description ?? '').toLowerCase().includes(q),
  );
  const selectedForms = selectedFormIds
    .map((id) => sendableForms.find((f) => f.id === id))
    .filter(Boolean) as Named[];
  const toggleForm = (id: string) =>
    setSelectedFormIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));
  const countLine = !q
    ? ''
    : what === 'bundles'
      ? `${bundleList.length} bundle${bundleList.length === 1 ? '' : 's'} contain${bundleList.length === 1 ? 's' : ''} "${query.trim()}"`
      : `${formList.length} form${formList.length === 1 ? '' : 's'} match "${query.trim()}"`;

  const selectedPacket = (packets.data ?? []).find((p) => p.id === selectedPacketId) ?? null;
  const selectedChips = selectedPacketId ? memberChips(selectedPacketId) : [];
  const patientLabel = useMemo(() => {
    const p = (patients.data ?? []).find((x) => x.id === patientId);
    return p ? `${p.firstName} ${p.lastName}` : '';
  }, [patients.data, patientId]);

  const nForms = selectedForms.length;
  const primaryLabel = editingScheduledId
    ? 'Update Scheduled Send'
    : timing === 'later'
      ? 'Schedule Send'
      : `Send ${what === 'bundles' ? 'Bundle' : nForms > 1 ? `${nForms} Forms` : 'Form'} ${DEST_SUFFIX[destination] ?? 'to Portal'}`;
  const pillLabel =
    what === 'bundles'
      ? (selectedPacket?.name ?? 'No bundle selected')
      : nForms === 0
        ? 'No forms selected'
        : nForms === 1
          ? selectedForms[0]!.name
          : `${nForms} forms selected`;
  // credifyfbs#312: how many forms the picked bundle holds (its loaded list, else the list count).
  const bundleForms = selectedPacket
    ? Math.max(packetForms[selectedPacket.id]?.length ?? 0, selectedPacket._count?.packetForms ?? 0)
    : 0;
  // credifyfbs#312: an empty bundle is not something to send (the pill stays grey).
  const hasSelection = what === 'bundles' ? !!selectedPacket && bundleForms > 0 : nForms > 0;
  // credifyfbs#308: today in the user's own time; earlier days can't be picked.
  const today = localDay();
  // credifyfbs#308 / #312: why the send can't go yet. The button stays disabled and says why.
  const blockReason = sendBlockReason({
    patientId,
    what,
    bundlePicked: !!selectedPacket,
    bundleForms,
    formsPicked: nForms,
    dueDate,
    timing: editingScheduledId ? 'later' : timing,
    sendDate: scheduleDate,
    sendTime: scheduleTime,
  });

  function setSendTiming(mode: 'now' | 'later') {
    setTiming(mode);
    if (mode === 'later') {
      if (!scheduleDate) setScheduleDate(plusDays(1));
      if (!scheduleTime) setScheduleTime('09:00');
    }
    if (mode === 'now' && editingScheduledId) setEditingScheduledId(null);
  }

  /** The POST/PUT /dispatch/scheduled bodies for the form as filled in (one per individual
   *  form, or one for the bundle), or null (with a message). */
  function buildScheduleBodies(): Record<string, unknown>[] | null {
    if (blockReason) {
      setMsg(blockReason);
      return null;
    }
    const when = new Date(`${scheduleDate}T${scheduleTime}`);
    const base = {
      patientId,
      destination,
      note: note.trim() || null,
      dueDate: dueDate || null,
      sendAt: when.toISOString(),
      assignToId: assignId || null,
    };
    return what === 'bundles'
      ? [{ ...base, packetId: selectedPacket!.id }]
      : selectedForms.map((f) => ({ ...base, formId: f.id }));
  }

  async function sendNow() {
    if (blockReason) {
      setMsg(blockReason);
      return;
    }
    setSending(true);
    setMsg(null);
    const base: Record<string, unknown> = { patientId, destination };
    if (note.trim()) base.note = note.trim();
    if (dueDate) base.dueInDays = daysFromToday(dueDate);
    const who = patientLabel || 'client';
    try {
      if (what === 'bundles') {
        await api.post('/dispatch', { ...base, packetId: selectedPacket!.id });
        setMsg(`✅ "${selectedPacket!.name}" sent to ${who}.`);
      } else {
        // fbs#158: one send per form (each is its own assignment for the client).
        const failed: string[] = [];
        for (const f of selectedForms) {
          try {
            await api.post('/dispatch', { ...base, formId: f.id });
          } catch (e) {
            // ehr#673: the client itself was refused, so no form goes; say why once.
            if (isClientRefusal(e)) throw e;
            failed.push(`${f.name} (${e instanceof Error ? e.message : String(e)})`);
          }
        }
        const sent = selectedForms.length - failed.length;
        setMsg(
          failed.length
            ? `${sent ? `✅ ${sent} sent to ${who}. ` : ''}Not sent: ${failed.join('; ')}`
            : nForms === 1
              ? `✅ "${selectedForms[0]!.name}" sent to ${who}.`
              : `✅ ${sent} forms sent to ${who}.`,
        );
        if (!failed.length) setSelectedFormIds([]);
      }
      history.reload();
    } catch (err) {
      setMsg(err instanceof Error ? err.message : String(err));
    } finally {
      setSending(false);
    }
  }

  async function saveSchedule() {
    const bodies = buildScheduleBodies();
    if (!bodies || !bodies.length) return;
    const body = bodies[0]!;
    const whenLabel = new Date(String(body.sendAt)).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      ...timeOpts(),
    });
    setSending(true);
    try {
      if (editingScheduledId) {
        await api.put(`/dispatch/scheduled/${editingScheduledId}`, body);
        // Forms added while editing are scheduled alongside it.
        for (const extra of bodies.slice(1)) await api.post('/dispatch/scheduled', extra);
        setEditingScheduledId(null);
        setMsg(`✅ Scheduled send updated — ${whenLabel}.`);
      } else {
        for (const b of bodies) await api.post('/dispatch/scheduled', b);
        const label = bodies.length > 1 ? `${bodies.length} forms` : 'Scheduled';
        setMsg(
          bodies.length > 1
            ? `📅 ${label} scheduled for ${whenLabel}. They will be sent then, even if this page is closed.`
            : `📅 Scheduled for ${whenLabel}. It will be sent then, even if this page is closed.`,
        );
      }
      setSendTiming('now');
      scheduledQ.reload();
    } catch (err) {
      setMsg(err instanceof Error ? err.message : String(err));
    } finally {
      setSending(false);
    }
  }

  function onPrimaryClick() {
    setMsg(null);
    if (editingScheduledId || timing === 'later') {
      void saveSchedule();
      return;
    }
    void sendNow();
  }

  function editScheduled(s: ScheduledSend) {
    if (s.packetId && (packets.data ?? []).some((p) => p.id === s.packetId)) {
      setWhat('bundles');
      setSelectedPacketId(s.packetId);
    } else if (s.formId) {
      setWhat('forms');
      setSelectedFormIds([s.formId]);
    }
    setPatientId(s.patientId);
    setDestination(s.destination || 'portal');
    setAssignId(s.assignToId || '');
    setNote(s.note || '');
    if (s.dueDate) setDueDate(s.dueDate);
    const when = schedDateTime(s);
    // A failed send is usually past its time: offer tomorrow at the same time instead.
    const next = when.getTime() > Date.now() ? when : new Date(Date.now() + 86_400_000);
    setScheduleDate(localDay(next));
    setScheduleTime(localTimeHHMM(when));
    setEditingScheduledId(s.id);
    setTiming('later');
  }

  async function cancelScheduled(s: ScheduledSend) {
    const okd = await askConfirm({
      confirmLabel: 'Cancel send',
      cancelLabel: 'Keep it',
      message: `Cancel the scheduled send of "${s.packetName ?? s.formName ?? 'this bundle'}" to ${s.patientName || 'this client'}? Scheduled for ${formatSchedWhen(s)} via ${DEST_LABEL[s.destination] ?? 'Client Portal'}. This can't be undone — you'd need to schedule it again.`,
    });
    if (!okd) return;
    try {
      await api.del(`/dispatch/scheduled/${s.id}`);
      if (editingScheduledId === s.id) {
        setEditingScheduledId(null);
        setTiming('now');
      }
      scheduledQ.reload();
    } catch (err) {
      setMsg(err instanceof Error ? err.message : String(err));
      scheduledQ.reload();
    }
  }

  /** The bundle editor: a new bundle (`from` null) or edit `from`. */
  function openBuilder(from: PacketWithCount | null) {
    autoSyncBuilder();
    setPbError('');
    setPbSearch('');
    if (from) {
      setPbEditId(from.id);
      setPbName(from.name);
      setPbDesc(from.description ?? '');
      setPbFormIds(memberChips(from.id).map((f) => f.id));
    } else {
      setPbEditId(null);
      setPbName('');
      setPbDesc('');
      setPbFormIds([]);
    }
    setPbOpen(true);
  }

  async function savePacket() {
    if (!pbName.trim()) {
      setPbError('Bundle name is required.');
      return;
    }
    setPbBusy(true);
    setPbError('');
    try {
      const body = { name: pbName.trim(), description: pbDesc.trim() || null, formIds: pbFormIds };
      if (pbEditId) await api.patch(`/packets/${pbEditId}`, body);
      else await api.post('/packets', body);
      setPbOpen(false);
      packets.reload();
    } catch (e) {
      setPbError(e instanceof Error ? e.message : String(e));
    } finally {
      setPbBusy(false);
    }
  }

  async function deletePacket(p: PacketWithCount) {
    if (!(await askConfirm(`Delete the bundle "${p.name}"? This can't be undone.`))) return;
    try {
      await api.del(`/packets/${p.id}`);
      if (selectedPacketId === p.id) setSelectedPacketId(null);
      packets.reload();
    } catch (e) {
      setMsg(e instanceof Error ? e.message : String(e));
    }
  }

  // #21 / fbs#156: the practice's Form Builder forms come in on their own (no button) when an
  // admin or manager opens Send or the bundle builder; imported forms (source "builder") then
  // appear in `sendableForms`. Throttled in lib/builderSync.
  function autoSyncBuilder() {
    if (!manage) return;
    setSyncing(true);
    void syncBuilderForms()
      .then((r) => {
        if (r && r.imported) forms.reload();
      })
      .finally(() => setSyncing(false));
  }
  useEffect(() => {
    autoSyncBuilder();
  }, [manage]);

  const pbQ = pbSearch.trim().toLowerCase();
  const pbResults = sendableForms.filter((f) => !pbQ || f.name.toLowerCase().includes(pbQ));
  const pbList = pbFormIds
    .map((id) => sendableForms.find((f) => f.id === id))
    .filter(Boolean) as Named[];

  const sortedScheduled = [...scheduled].sort(
    (a, b) => schedDateTime(a).getTime() - schedDateTime(b).getTime(),
  );

  return (
    <div className="dispatch-layout">
      {/* LEFT — packet templates */}
      <div className="packet-col">
        <div className="section-label" style={{ flex: 'none' }}>
          What to send
        </div>
        {/* fbs#158: a bundle, or individual forms */}
        <div className="seg-toggle fh-what" role="tablist" aria-label="What to send">
          <button
            type="button"
            role="tab"
            aria-selected={what === 'bundles'}
            className={`seg-btn${what === 'bundles' ? ' active' : ''}`}
            onClick={() => setWhat('bundles')}
          >
            Bundles
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={what === 'forms'}
            className={`seg-btn${what === 'forms' ? ' active' : ''}`}
            onClick={() => setWhat('forms')}
          >
            Individual forms
          </button>
        </div>
        <div className="pb-form-search-wrap fh-search">
          <span className="pb-search-icon">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="7" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
          </span>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search bundles or forms"
            aria-label="Search bundles or forms"
            autoComplete="off"
          />
          {query && (
            <button
              type="button"
              className="fh-search-clear"
              aria-label="Clear search"
              onClick={() => setQuery('')}
            >
              ✕
            </button>
          )}
        </div>
        {countLine && <div className="fh-search-count">{countLine}</div>}
        {/* fbs#158: single forms are one joined list, no gaps between them */}
        <div className={`packet-list${what === 'forms' ? ' fh-form-list' : ''}`}>
          {what === 'forms' ? (
            formList.length === 0 ? (
              <div className="fh-list-empty">
                {q ? 'No forms match.' : 'No forms to send yet. Build them in the Form Builder.'}
              </div>
            ) : (
              formList.map((f) => (
                <label
                  key={f.id}
                  className={`fh-form-pick${selectedFormIds.includes(f.id) ? ' selected' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={selectedFormIds.includes(f.id)}
                    onChange={() => toggleForm(f.id)}
                  />
                  <span className="fh-form-pick-name">
                    <Hl text={f.name} q={q} />
                  </span>
                  {f.description && <span className="fh-form-pick-desc">{f.description}</span>}
                </label>
              ))
            )
          ) : bundleList.length === 0 ? (
            <div
              style={{
                padding: 16,
                textAlign: 'center',
                fontSize: 12.5,
                color: 'var(--text-muted-2)',
              }}
            >
              {q ? 'No bundles match.' : 'No bundles yet. Use “+ New Bundle” to create one.'}
            </div>
          ) : (
            bundleList.map((p) => (
              <div
                key={p.id}
                className={`packet-card${selectedPacketId === p.id ? ' selected' : ''}`}
                onClick={() => setSelectedPacketId(p.id)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setSelectedPacketId(p.id);
                  }
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                  <div className="packet-card-name" style={{ flex: 1, minWidth: 0 }}>
                    <Hl text={p.name} q={q} />
                  </div>
                  {manage && (
                    <>
                      <button
                        type="button"
                        className="packet-icon-btn"
                        title="Edit bundle"
                        aria-label="Edit bundle"
                        style={{ color: 'var(--text-muted)' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          openBuilder(p);
                        }}
                      >
                        <svg
                          width="13"
                          height="13"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                        </svg>
                      </button>
                      <button
                        type="button"
                        className="packet-icon-btn"
                        title="Delete bundle"
                        aria-label="Delete bundle"
                        style={{ color: 'var(--red)' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          void deletePacket(p);
                        }}
                      >
                        <svg
                          width="13"
                          height="13"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <polyline points="3 6 5 6 21 6" />
                          <path d="M19 6l-2 14H7L5 6" />
                          <path d="M10 11v6M14 11v6" />
                          <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                        </svg>
                      </button>
                    </>
                  )}
                </div>
                <div className="packet-card-meta">
                  {p.description ? `${p.description} · ` : ''}
                  {packetForms[p.id] ? memberChips(p.id).length : (p._count?.packetForms ?? 0)}{' '}
                  forms
                </div>
                <div className="packet-card-forms">
                  {memberChips(p.id).map((f) => (
                    <span className="form-chip" key={f.id}>
                      <Hl text={f.name} q={q} />
                    </span>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
        {manage && what === 'bundles' && (
          <button
            className="fh-btn sm primary"
            style={{ marginTop: 10, flex: 'none' }}
            onClick={() => openBuilder(null)}
          >
            + New Bundle
          </button>
        )}
        {what === 'forms' && nForms > 0 && (
          <div className="fh-pick-count">
            {nForms} selected ·{' '}
            <button type="button" className="fh-link" onClick={() => setSelectedFormIds([])}>
              Clear
            </button>
          </div>
        )}
      </div>

      {/* RIGHT — send form */}
      <div className="fh-card">
        <div className="fh-card-head">
          <h3>Send to Client</h3>
          <span className={hasSelection ? 'pill green' : 'pill muted'}>{pillLabel}</span>
        </div>
        <div className="card-body">
          {editingScheduledId && (
            <div className="sched-edit-banner">
              <span>✏️ Editing a scheduled send</span>
              <button
                className="fh-btn sm"
                type="button"
                onClick={() => {
                  setEditingScheduledId(null);
                  setSendTiming('now');
                }}
              >
                Discard changes
              </button>
            </div>
          )}
          <div className="field-row">
            <div className="field-group">
              <label htmlFor="send-patient">Client</label>
              <Dd
                value={patientId}
                onChange={setPatientId}
                ariaLabel="Client"
                placeholder={clientPlaceholder(user?.role)}
                // credifyfbs#309: each client once; same names show their Client ID
                options={clientOptions(patients.data ?? [])}
              />
            </div>
            <div className="field-group">
              <label htmlFor="send-due">Due Date</label>
              {/* credifyfbs#308: today or later (on or after the send date when scheduled) */}
              <DateField
                id="send-due"
                value={dueDate}
                onChange={setDueDate}
                min={timing === 'later' && scheduleDate > today ? scheduleDate : today}
              />
            </div>
          </div>
          <div className="field-row">
            <div className="field-group">
              <label htmlFor="send-dest">Destination</label>
              <Dd
                value={destination}
                onChange={setDestination}
                searchable={false}
                ariaLabel="Destination"
                options={[
                  { value: 'portal', label: 'Client Portal' },
                  { value: 'queue', label: 'Clinician Queue' },
                  { value: 'both', label: 'Both' },
                ]}
              />
            </div>
            <div className="field-group">
              <label htmlFor="send-assign">Assign Clinician</label>
              <Dd
                value={assignId}
                onChange={setAssignId}
                ariaLabel="Assign Clinician"
                placeholder="— Optional —"
                options={(providers.data ?? []).map((p) => ({
                  value: p.id,
                  label: providerName(p),
                }))}
              />
            </div>
          </div>
          <div className="field-row">
            <div className="field-group" style={{ flex: 2 }}>
              <label htmlFor="send-note">Note to Client (optional)</label>
              <textarea
                id="send-note"
                rows={2}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="e.g. Please complete before your intake appointment on Thursday."
              />
            </div>
          </div>

          {what === 'forms' && nForms > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div className="section-label">Forms to send ({nForms})</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
                {selectedForms.map((f) => (
                  <span className="form-chip fh-chip-x fh-pill" key={f.id}>
                    {f.name}
                    <button
                      type="button"
                      aria-label={`Remove ${f.name}`}
                      onClick={() => toggleForm(f.id)}
                    >
                      ✕
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}
          {what === 'bundles' && selectedPacket && selectedChips.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div className="section-label">Forms in this bundle</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
                {selectedChips.map((f) => (
                  <span className="form-chip fh-pill" key={f.id}>
                    {f.name}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="field-row">
            <div className="field-group" style={{ flex: 'none' }}>
              <label id="send-timing-label">Send Timing</label>
              {/* fbs#158: a radio choice, not a toggle */}
              <div className="timing-radios" role="radiogroup" aria-labelledby="send-timing-label">
                <label className={`timing-radio${timing === 'now' ? ' on' : ''}`}>
                  <input
                    type="radio"
                    name="send-timing"
                    checked={timing === 'now'}
                    onChange={() => setSendTiming('now')}
                  />
                  Send now
                </label>
                <label className={`timing-radio${timing === 'later' ? ' on' : ''}`}>
                  <input
                    type="radio"
                    name="send-timing"
                    checked={timing === 'later'}
                    onChange={() => setSendTiming('later')}
                  />
                  Schedule for later
                </label>
              </div>
            </div>
          </div>
          {timing === 'later' && (
            <div className="field-row">
              <div className="field-group">
                <label htmlFor="scheduleDate">Send Date</label>
                <DateField
                  id="scheduleDate"
                  value={scheduleDate}
                  onChange={setScheduleDate}
                  min={today}
                />
              </div>
              <div className="field-group">
                <label htmlFor="scheduleTime">Send Time</label>
                <TimeField id="scheduleTime" value={scheduleTime} onChange={setScheduleTime} />
              </div>
            </div>
          )}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <button
              className="fh-btn primary"
              onClick={onPrimaryClick}
              disabled={sending || !!blockReason}
              aria-describedby={blockReason ? 'send-block-reason' : undefined}
            >
              <SendSvg /> <span>{sending ? 'Sending…' : primaryLabel}</span>
            </button>
            {/* fbs#163: no "Save as Bundle" here; bundles are made with "+ New Bundle". */}
            {msg && msg !== blockReason && (
              <span className="muted" style={{ fontSize: 12.5 }}>
                {msg}
              </span>
            )}
            {/* credifyfbs#308 / #312: why the button is disabled */}
            {blockReason && (
              <span id="send-block-reason" className="muted" style={{ fontSize: 12.5 }}>
                {blockReason}
              </span>
            )}
          </div>
        </div>

        <div style={{ padding: '0 20px 4px' }}>
          <div className="section-label" style={{ marginBottom: 12 }}>
            Scheduled Sends
          </div>
          {sortedScheduled.length === 0 ? (
            <div className="scheduled-empty">No scheduled sends.</div>
          ) : (
            sortedScheduled.map((s) => (
              <div
                key={s.id}
                className={`scheduled-row${editingScheduledId === s.id ? ' editing' : ''}`}
              >
                <div className="sr-patient">{s.patientName || 'Client'}</div>
                <div className="sr-packet">
                  {s.packetName ?? s.formName ?? ''}
                  {s.status === 'failed' && (
                    <div className="sr-failed" title={s.error ?? undefined}>
                      Not sent: {s.error || 'the send failed'}. Edit to reschedule.
                    </div>
                  )}
                </div>
                <div>
                  <span className="pill muted">{DEST_LABEL[s.destination] ?? 'Client Portal'}</span>
                </div>
                <div className="sr-when">
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="3" y="4" width="18" height="18" rx="2" />
                    <line x1="16" y1="2" x2="16" y2="6" />
                    <line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                  </svg>
                  {formatSchedWhen(s)}
                </div>
                <div className="actions">
                  <button className="btn sm" onClick={() => editScheduled(s)}>
                    Edit
                  </button>
                  <button className="btn sm danger" onClick={() => cancelScheduled(s)}>
                    Cancel
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        <div style={{ padding: '16px 20px 20px' }}>
          <div className="section-label" style={{ marginBottom: 12 }}>
            Recent Sends
          </div>
          <Async
            state={history}
            emptyWhen={(h) => h.length === 0}
            emptyMessage="No dispatches yet."
          >
            {(list) => (
              <>
                {list.map((dsp) => (
                  <div className="dispatch-history-row" key={dsp.id}>
                    <div className="dh-patient">
                      {dsp.patient ? `${dsp.patient.firstName} ${dsp.patient.lastName}` : '—'}
                    </div>
                    <div className="dh-packet">{dsp.form?.name ?? dsp.packet?.name ?? '—'}</div>
                    <div>
                      <span className="pill muted">
                        {DEST_LABEL[dsp.destination] ?? dsp.destination}
                      </span>
                    </div>
                    <div className="dh-date">{fmtSent(dsp.sentAt)}</div>
                  </div>
                ))}
              </>
            )}
          </Async>
        </div>
      </div>

      {/* PACKET BUILDER SLIDE-IN */}
      <div className={`pb-overlay${pbOpen ? ' open' : ''}`} onClick={() => setPbOpen(false)} />
      <div
        className={`pb-panel${pbOpen ? ' open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="New Bundle Builder"
      >
        <div className="pb-head">
          <h2>
            <SaveSvg /> <span>{pbEditId ? 'Edit Bundle' : 'New Bundle'}</span>
          </h2>
          <button className="close-btn" aria-label="Close" onClick={() => setPbOpen(false)}>
            ✕
          </button>
        </div>
        <div className="pb-body">
          <div>
            <div className="field-group" style={{ marginBottom: 12 }}>
              <label>
                Bundle Name <span style={{ color: 'var(--red)' }}>*</span>
              </label>
              <input
                type="text"
                value={pbName}
                onChange={(e) => setPbName(e.target.value)}
                placeholder="e.g. SUD Re-entry Bundle"
              />
            </div>
            <div className="field-group">
              <label>Description</label>
              <input
                type="text"
                value={pbDesc}
                onChange={(e) => setPbDesc(e.target.value)}
                placeholder="Brief description of this bundle"
              />
            </div>
          </div>
          <div>
            <div
              className="section-label"
              style={{
                marginBottom: 8,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 8,
              }}
            >
              <span>Add Forms</span>
              {manage && (
                <span className="pb-sync-note" aria-live="polite">
                  {syncing
                    ? 'Getting the latest Form Builder forms…'
                    : 'Form Builder forms sync automatically'}
                </span>
              )}
            </div>
            <div className="pb-form-search-wrap" style={{ marginBottom: 8 }}>
              <span className="pb-search-icon">
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="11" cy="11" r="7" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </span>
              <input
                type="text"
                value={pbSearch}
                onChange={(e) => setPbSearch(e.target.value)}
                placeholder="Search forms..."
                autoComplete="off"
              />
            </div>
            <div className="pb-results">
              {pbResults.length === 0 ? (
                <div className="form-search-empty">No forms match.</div>
              ) : (
                pbResults.map((f) => (
                  <label className="pb-result-item" key={f.id}>
                    <input
                      type="checkbox"
                      checked={pbFormIds.includes(f.id)}
                      onChange={() =>
                        setPbFormIds((ids) =>
                          ids.includes(f.id) ? ids.filter((x) => x !== f.id) : [...ids, f.id],
                        )
                      }
                    />
                    <span className="pb-result-name">{f.name}</span>
                    {f.description && <span className="pb-result-meta">{f.description}</span>}
                  </label>
                ))
              )}
            </div>
          </div>
          <div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 8,
              }}
            >
              <div className="section-label" style={{ marginBottom: 0 }}>
                Forms in Bundle{' '}
                <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>
                  ({pbFormIds.length})
                </span>
              </div>
              <span style={{ fontSize: 11.5, color: 'var(--text-muted-2)' }}>Drag to reorder</span>
            </div>
            <div className="pb-form-list">
              {pbList.length === 0 ? (
                <div className="pb-empty-list">No forms added yet.</div>
              ) : (
                pbList.map((f, i) => (
                  <div
                    key={f.id}
                    className={`pb-form-row${dragIdx === i ? ' dragging' : ''}${dragOver === i && dragIdx !== i ? ' drag-over' : ''}`}
                    draggable
                    onDragStart={() => setDragIdx(i)}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setDragOver(i);
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      if (dragIdx === null || dragIdx === i) return;
                      setPbFormIds((ids) => {
                        const next = [...ids];
                        const [moved] = next.splice(dragIdx, 1);
                        next.splice(i, 0, moved!);
                        return next;
                      });
                      setDragIdx(null);
                      setDragOver(null);
                    }}
                    onDragEnd={() => {
                      setDragIdx(null);
                      setDragOver(null);
                    }}
                  >
                    <span className="pb-drag-handle">
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        stroke="none"
                      >
                        <circle cx="8" cy="6" r="1.6" />
                        <circle cx="16" cy="6" r="1.6" />
                        <circle cx="8" cy="12" r="1.6" />
                        <circle cx="16" cy="12" r="1.6" />
                        <circle cx="8" cy="18" r="1.6" />
                        <circle cx="16" cy="18" r="1.6" />
                      </svg>
                    </span>
                    <span className="pb-row-name">{f.name}</span>
                    <button
                      className="pb-row-remove"
                      title="Remove form"
                      onClick={() => setPbFormIds((ids) => ids.filter((x) => x !== f.id))}
                    >
                      ✕
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
          {pbError && <span className="badge urgent">{pbError}</span>}
        </div>
        <div className="pb-foot">
          <button className="fh-btn" onClick={() => setPbOpen(false)} disabled={pbBusy}>
            Cancel
          </button>
          <button className="fh-btn primary" onClick={savePacket} disabled={pbBusy}>
            {pbBusy ? 'Saving…' : 'Save Bundle'}
          </button>
        </div>
      </div>
    </div>
  );
}
